import type { Column } from 'react-table';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { IconCertificate, IconFlexPoolOnlyJob } from 'assets/img';
import { ContextMenuButton, StatusIndicator } from 'redesign';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { convertToExperienceFormat, useAvailabilityStatusAndColor } from './helpers';
import { useGetShiftDetailsColumnDefinitionBase } from '../../shift-detail-modal/components/tabs/shared/columns/use-get-shift-details-column-definition-base';
import {
  FlexWorkerCellProp,
  FlexworkerColumnDefinition,
} from '../flex-workers-for-work-table.types';

export const useGetFlexworkerColumnDefinition = (
  hideAvailabilityColumn: boolean
): Column<FlexworkerColumnDefinition>[] => {
  const [flexWorkerImage, flexWorker] = useGetShiftDetailsColumnDefinitionBase();
  const { t } = useTranslation('hq-screen-specific');

  flexWorkerImage.width = '65px';
  flexWorker.width = '200px';

  return useMemo(() => {
    const hasRequiredCertificates: Column<FlexworkerColumnDefinition> = {
      Header: () => (
        <Header>
          {t('work.add_flexworker.table_headers.has_certificate', { ns: 'hq-screen-specific' })}
        </Header>
      ),
      accessor: 'hasRequiredCertificates',
      Cell: CertificateChecksCell,
      width: '75px',
    };

    const telephoneNumber: Column<FlexworkerColumnDefinition> = {
      Header: () => (
        <Header>
          {t('work.add_flexworker.table_headers.phone', { ns: 'hq-screen-specific' })}
        </Header>
      ),
      accessor: 'phoneNumber',
      Cell: PhoneCell,
      width: '160px',
    };

    const flexPool: Column<FlexworkerColumnDefinition> = {
      Header: () => (
        <Header>
          {t('work.add_flexworker.table_headers.is_in_flexpool', { ns: 'hq-screen-specific' })}
        </Header>
      ),
      accessor: 'inFlexPool',
      Cell: FlexpoolCell,
      width: '75px',
    };

    const distance: Column<FlexworkerColumnDefinition> = {
      Header: () => (
        <Header>
          {t('work.add_flexworker.table_headers.distance', { ns: 'hq-screen-specific' })}
        </Header>
      ),
      accessor: 'distance',
      Cell: DistanceCell,
      width: '75px',
    };

    const availability: Column<FlexworkerColumnDefinition> = {
      Header: () => (
        <Header>
          {t('work.add_flexworker.table_headers.availability', { ns: 'hq-screen-specific' })}
        </Header>
      ),
      accessor: 'availability',
      Cell: AvailabilityCell,
      width: '150px',
    };

    const experience: Column<FlexworkerColumnDefinition> = {
      Header: () => (
        <Header>
          {t('work.add_flexworker.table_headers.work_experiences', { ns: 'hq-screen-specific' })}
        </Header>
      ),
      accessor: 'experienceInMonths',
      Cell: ExperienceCell,
      width: '150px',
    };

    const actions: Column<FlexworkerColumnDefinition> = {
      Header: '',
      accessor: 'actions',
      Cell: ActionCell,
      width: '88px',
    };

    // /*
    // This is temporary solution to hide availability column in project detail.
    // Simon will came up with better solution to refactor this.
    // */

    if (hideAvailabilityColumn) {
      return [
        flexWorkerImage,
        flexWorker,
        telephoneNumber,
        hasRequiredCertificates,
        flexPool,
        distance,
        experience,
        actions,
      ];
    }

    return [
      flexWorkerImage,
      flexWorker,
      telephoneNumber,
      hasRequiredCertificates,
      flexPool,
      distance,
      availability,
      experience,
      actions,
    ];
  }, [hideAvailabilityColumn, t, flexWorkerImage, flexWorker]);
};
const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <Typography variant="body2" color="secondary" style={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

const CertificateChecksCell = ({ value }: FlexWorkerCellProp<'hasRequiredCertificates'>) => {
  return value ? (
    <Tooltip
      title="Deze flexwerker is in het bezit van alle benodigde certificaten"
      aria-label="certificate-check"
    >
      <Box paddingLeft={1}>
        <IconCertificate data-testid="certificate-check-icon" width={24} />
      </Box>
    </Tooltip>
  ) : null;
};

const PhoneCell = (cell: FlexWorkerCellProp<'phoneNumber'>) => {
  return <Typography noWrap>{cell.value}</Typography>;
};

const FlexpoolCell = (cell: FlexWorkerCellProp<'inFlexPool'>) => {
  return cell.value ? <IconFlexPoolOnlyJob data-testid="in-flex-pool-icon" width={24} /> : null;
};

const DistanceCell = (cell: FlexWorkerCellProp<'distance'>) => {
  return (
    <Typography>
      {cell.value === null ? '-' : cell.value === 0 ? '<1 km' : `${cell.value} km`}
    </Typography>
  );
};

const ExperienceCell = (cell: FlexWorkerCellProp<'distance'>) => {
  return <Typography noWrap>{convertToExperienceFormat(cell.value)}</Typography>;
};

const AvailabilityCell = (cell: FlexWorkerCellProp<'availability'>) => {
  const theme = useTheme();
  const { availability, color } = useAvailabilityStatusAndColor(cell.value);
  return (
    <StatusIndicator
      backgroundColor={color}
      label={availability}
      color={theme.palette.lwBlack[100]}
    />
  );
};

const ActionCell = ({ value, getRowActions }: FlexWorkerCellProp<'actions'>) => {
  const actions = getRowActions(value.flexWorkerId, value.entityId, value.flexWorkerName);

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`claimant-actions-menu-${value.flexWorkerId}`}>
      {actions}
    </ContextMenuButton>
  );
};
